import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { guardLoginUrl } from '../modules/auth/sessionSignals';

/**
 * @route ProtectedRoute
 * @description Standard 7: Zero-Trust Guard.
 */
/**
 * `roles` — optional. When given, the signed-in person must hold one of them,
 * counting BOTH the roles they have and the one they are currently acting as,
 * since this app lets a person be reviewer and customer at once.
 *
 * `fallback` is where a signed-in person who lacks the role is sent. It is a
 * separate prop rather than a fixed 403 on purpose: for most role-gated pages
 * the useful answer is not "you may not see this", it is "here is how you get
 * it". A customer who lands on the reviewer ladder wants the application, not
 * a locked door.
 */
export const ProtectedRoute = ({ children, roles = null, fallback = '/' }) => {
  const { loading, isAuthenticated, user, activeRole, availableRoles } = useAuthContext();
  const location = useLocation();

  // 🛡️ Logic: If the AuthOrchestrator is still fetching the profile,
  // do NOT redirect. Show a neutral loading shell to avoid a white flash.
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
          <p className="text-sm text-gray-400 font-medium">Loading…</p>
        </div>
      </div>
    );
  }

  // Logic: Only redirect once we are 100% sure the session is invalid
  if (!isAuthenticated) {
    // Somebody who just signed out is told so, not asked to log in as if
    // refused (GC-092) — guardLoginUrl reads the sign-out signal.
    const next = `${location.pathname || '/'}${location.search || ''}${location.hash || ''}`;
    return <Navigate to={guardLoginUrl({ next })} replace />;
  }

  if (roles?.length) {
    const want = roles.map((r) => String(r).toLowerCase());
    const held = [
      user?.role,
      activeRole,
      ...(Array.isArray(availableRoles) ? availableRoles : []),
      ...(Array.isArray(user?.assignedRoles) ? user.assignedRoles : [])
    ].filter(Boolean).map((r) => String(r).toLowerCase());

    // Admins are never locked out of a role-gated page: they support the
    // people who use it, and a support call that cannot see the screen is a
    // support call that cannot help.
    const privileged = held.some((r) => r === 'admin' || r === 'super_admin');
    if (!privileged && !held.some((r) => want.includes(r))) {
      // Name the refusal on the fallback page (RefusalNotice reads these):
      // the page asked for, the roles it needs, and the way on.
      const params = new URLSearchParams({ reason: 'insufficient_role', from: location.pathname || '/', needs: want.join(',') });
      const sep = fallback.includes('?') ? '&' : '?';
      return <Navigate to={`${fallback}${sep}${params.toString()}`} replace />;
    }
  }

  return children;
};
